/**
 * Страница профиля.
 *
 *  /profile           → свой (3 вкладки: Основное / Оформление / Настройки)
 *  /profile/$userId   → чужой (read-only карточка)
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { useAuth } from "@/auth/AuthContext";
import {
  uploadMyAvatar, deleteMyAvatar, updateMyProfile,
  fetchUserPublic,
  fetchTelegramStatus, bindTelegram, unbindTelegram,
  fetchPushStatus, muteNotifications, unmuteNotifications,
  type PublicUser,
} from "@/api/dashboard";
import { fmtLastSeen, isOnline } from "@/lib/presence";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { api } from "@/api/client";



const PROFILE_DEPT_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
};

function roleLabel(role: string, isSuperuser: boolean): string {
  if (role === "owner") return "Собственник";
  if (role === "admin") return "Администратор";
  if (role === "head_op") return "Руководитель ОП";
  if (role === "op_manager") return "Менеджер ОП";
  if (role === "kc_manager") return "Менеджер КЦ";
  if (role === "assistant") return "Ассистент";
  if (role === "field_engineer") return "Выездной инженер";
  if (role === "engineer") return "Инженер";
  if (role === "foreman") return "Бригадир";
  if (role === "builder") return "Строитель";
  if (isSuperuser) return "Администратор";
  return role || "Менеджер";
}

const DEPARTMENT_LABELS: Record<string, string> = {
  admin: "Администрация",
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  finance: "Финансы",
  supply: "Отдел снабжения",
};

function deptLabels(depts: string[] | undefined | null): string {
  if (!depts || depts.length === 0) return "—";
  return depts.map(d => DEPARTMENT_LABELS[d] || d).join(", ");
}


export function ProfilePage() {
  const params = useParams({ strict: false }) as { userId?: string };
  const { user, refresh } = useAuth();
  const isOwnProfile = !params.userId || params.userId === user?.id;

  const otherQ = useQuery({
    queryKey: ["profiles", params.userId],
    queryFn: () => fetchUserPublic(params.userId!),
    enabled: !isOwnProfile && !!params.userId,
    refetchInterval: 30000,
  });

  // Перерендер раз в 60s — чтобы «5 мин назад» не «застывало»
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setTick((x) => x + 1), 60000);
    return () => window.clearInterval(t);
  }, []);

  if (isOwnProfile) {
    if (!user) return null;
    return <OwnProfileTabs user={user} refresh={refresh}/>;
  }

  if (otherQ.isLoading) return <div style={{ padding: 32, color: "var(--mute)" }}>Загрузка…</div>;
  if (otherQ.isError || !otherQ.data) {
    return <div style={{ padding: 32, color: "var(--brick)" }}>Профиль не найден</div>;
  }
  return <PublicProfileView profile={otherQ.data}/>;
}


function PublicProfileView({ profile }: { profile: PublicUser }) {
  const online = isOnline(profile.last_seen_at);
  const role = roleLabel(profile.role, false);

  return (
    <div className="page" style={{ maxWidth: 720, margin: "0 auto", padding: "32px 0" }}>
      <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginBottom: 4 }}>Профиль</h1>
      <div style={{ color: "var(--mute)", marginBottom: 28, fontSize: 13.5 }}>
        Карточка участника команды
      </div>

      <div className="card" style={{ padding: 28 }}>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <Avatar
            name={profile.display_name || "Участник"}
            userId={profile.id}
            avatarFilename={profile.avatar_filename}
            color="#4A413B"
            size={96}
            online={online}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, marginBottom: 2 }}>
              {profile.display_name || "Без имени"}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 4 }}>
              {role}
              {profile.position && profile.position !== role && (
                <span style={{ color: "var(--mute)", marginLeft: 8, fontSize: 12 }}>· {profile.position}</span>
              )}
            </div>
            <div style={{
              fontSize: 12,
              color: online ? "var(--green)" : "var(--mute)",
              fontWeight: online ? 600 : 400,
            }}>
              {fmtLastSeen(profile.last_seen_at)}
            </div>
            {profile.departments && profile.departments.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {profile.departments.map(d => PROFILE_DEPT_LABELS[d] && (
                  <span key={d} className={`dept-tag dept-tag-${d}`}>{PROFILE_DEPT_LABELS[d]}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {profile.bio && (
          <>
            <div style={{ height: 1, background: "var(--line)", margin: "24px 0" }}/>
            <div>
              <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 6 }}>Описание</div>
              <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", color: "var(--ink-2)" }}>
                {profile.bio}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


type Tab = "main" | "appearance" | "settings";

function OwnProfileTabs({ user, refresh }: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  refresh: () => Promise<void>;
}) {
  const [tab, setTab] = useState<Tab>("main");
  return (
    <div className="profile-page" style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px 64px" }}>
      <h1 style={{ fontFamily: "var(--serif)", fontSize: 26, marginBottom: 16 }}>Профиль</h1>
      <div className="profile-tabs">
        <button className={`profile-tab${tab === "main" ? " active" : ""}`} onClick={() => setTab("main")}>Основное</button>
        <button className={`profile-tab${tab === "appearance" ? " active" : ""}`} onClick={() => setTab("appearance")}>Оформление</button>
        <button className={`profile-tab${tab === "settings" ? " active" : ""}`} onClick={() => setTab("settings")}>Настройки</button>
      </div>
      <div className="profile-tab-body">
        {tab === "main" && <MainTab user={user} refresh={refresh}/>}
        {tab === "appearance" && <AppearanceTab/>}
        {tab === "settings" && <SettingsTab/>}
      </div>
    </div>
  );
}


function MainTab({ user, refresh }: {
  user: NonNullable<ReturnType<typeof useAuth>["user"]>;
  refresh: () => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [position, setPosition] = useState(user.position ?? "");
  const [bio, setBio] = useState(user.bio ?? "");
  const [phone, setPhone] = useState((user as { phone?: string | null }).phone ?? "");
  const [birthday, setBirthday] = useState<string>(
    (user as { birthday?: string | null }).birthday
      ? `2000-${(user as { birthday?: string | null }).birthday}`
      : ""
  );
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayName(user.display_name ?? "");
    setPosition(user.position ?? "");
    setBio(user.bio ?? "");
    setPhone((user as { phone?: string | null }).phone ?? "");
    const bday = (user as { birthday?: string | null }).birthday;
    setBirthday(bday ? `2000-${bday}` : "");
  }, [user.display_name, user.position, user.bio, (user as { phone?: string | null }).phone, (user as { birthday?: string | null }).birthday]);

  const avatarMut = useMutation({
    mutationFn: (file: File) => uploadMyAvatar(file),
    onSuccess: async () => { setError(null); await refresh(); },
    onError: (e: Error) => setError(e.message || "Не удалось загрузить аватар"),
  });

  const deleteAvatarMut = useMutation({
    mutationFn: () => deleteMyAvatar(),
    onSuccess: async () => { setError(null); await refresh(); },
  });

  const profileMut = useMutation({
    mutationFn: (patch: { display_name: string; position: string | null; bio: string | null; birthday: string | null; phone: string | null }) =>
      updateMyProfile(patch),
    onSuccess: async () => {
      setError(null);
      setSavedAt(Date.now());
      await refresh();
    },
    onError: (e: Error) => setError(e.message || "Не удалось сохранить"),
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { setError("Только изображения"); return; }
    if (f.size > 2 * 1024 * 1024) { setError("Файл больше 2 МБ"); return; }
    avatarMut.mutate(f);
  }

  function onSubmitProfile(e: FormEvent) {
    e.preventDefault();
    const name = displayName.trim();
    if (!name) { setError("Имя не может быть пустым"); return; }
    const bdayMmdd = birthday ? birthday.slice(5) : null;
    profileMut.mutate({
      display_name: name,
      position: position.trim() || null,
      bio: bio.trim() || null,
      birthday: bdayMmdd,
      phone: phone.trim() || null,
    });
  }

  const role = roleLabel(user.role || "op_manager", user.is_superuser);
  const online = isOnline(user.last_seen_at);
  const userBday = (user as { birthday?: string | null }).birthday ?? null;
  const userPhone = (user as { phone?: string | null }).phone ?? null;
  const dirty = displayName.trim() !== (user.display_name ?? "")
    || (position.trim() || null) !== (user.position ?? null)
    || (bio.trim() || null) !== (user.bio ?? null)
    || (birthday ? birthday.slice(5) : null) !== userBday
    || (phone.trim() || null) !== userPhone;

  return (
    <div className="profile-card">
      <div className="profile-avatar-row">
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            name={displayName || "Я"}
            userId={user.id}
            avatarFilename={user.avatar_filename}
            color="#AB2B2B"
            size={96}
            online={online}
          />
          {avatarMut.isPending && (
            <div className="profile-avatar-overlay">загрузка…</div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <div className="profile-avatar-actions">
            <input type="file" ref={fileInputRef} accept="image/*" hidden onChange={onPickFile}/>
            <button type="button" className="profile-btn-primary"
              onClick={() => fileInputRef.current?.click()} disabled={avatarMut.isPending}>
              <Icon name="image" size={12}/> Загрузить аватар
            </button>
            {user.avatar_filename && (
              <button type="button" className="profile-btn-ghost"
                onClick={() => deleteAvatarMut.mutate()} disabled={deleteAvatarMut.isPending}>
                <Icon name="trash" size={12}/> Удалить
              </button>
            )}
          </div>
          <div className="profile-hint">
            JPG, PNG, WebP или GIF. До 2 МБ. Квадратное соотношение — оптимально.
          </div>
          <div style={{ fontSize: 12, color: online ? "var(--tg-accent, #4fae4e)" : "var(--mute)", fontWeight: online ? 600 : 400 }}>
            {fmtLastSeen(user.last_seen_at)}
          </div>
        </div>
      </div>

      <div className="profile-divider"/>

      <form onSubmit={onSubmitProfile} className="profile-form">
        <Field label="Отображаемое имя">
          <input type="text" className="profile-input"
            value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120} placeholder="Как вас будут видеть в чате"/>
        </Field>

        <Field label="Отдел">
          <div className="profile-readonly">{deptLabels(user.departments)}</div>
        </Field>

        <Field label="Должность (системная)">
          <div className="profile-readonly">{role}</div>
        </Field>

        <Field label="Должность в команде">
          <input type="text" className="profile-input"
            value={position} onChange={(e) => setPosition(e.target.value)}
            maxLength={120} placeholder="Например: «Старший менеджер по продажам»"/>
        </Field>

        <Field label="ID">
          <div className="profile-readonly profile-readonly-mono">{user.id}</div>
        </Field>

        <Field label="Номер телефона">
          <input type="tel" className="profile-input"
            value={phone} onChange={(e) => setPhone(e.target.value)}
            maxLength={32} placeholder="+7 ..."/>
        </Field>

        <Field label="День рождения" hint="Год не отображается у других участников">
          <input type="date" className="profile-input"
            value={birthday} onChange={(e) => setBirthday(e.target.value)}
            style={{ maxWidth: 200 }}/>
        </Field>

        <Field label="Описание профиля">
          <textarea className="profile-input"
            value={bio} onChange={(e) => setBio(e.target.value)}
            maxLength={2000} placeholder="Зона ответственности, контакты, предпочтения по связи…"
            rows={4}
            style={{ resize: "vertical", fontFamily: "var(--sans)" }}/>
        </Field>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
          <button type="submit" className="profile-btn-primary"
            disabled={profileMut.isPending || !dirty}>
            {profileMut.isPending ? "…" : "Сохранить"}
          </button>
          {savedAt && Date.now() - savedAt < 3000 && (
            <span style={{ color: "var(--tg-accent, #4fae4e)", fontSize: 12 }}>
              <Icon name="check" size={12}/> Сохранено
            </span>
          )}
        </div>
      </form>

      {error && <div className="profile-error">{error}</div>}
    </div>
  );
}


function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="profile-field">
      <label className="profile-field-label">{label}</label>
      {children}
      {hint && <div className="profile-field-hint">{hint}</div>}
    </div>
  );
}


function AppearanceTab() {
  const [accent, setAccent] = useState<string>(() => {
    try { return localStorage.getItem("mng-accent") || "green"; } catch { return "green"; }
  });
  const [bg, setBg] = useState<string>(() => {
    try { return localStorage.getItem("mng-bg") || "green"; } catch { return "green"; }
  });

  useEffect(() => {
    try { localStorage.setItem("mng-accent", accent); } catch { /* ignore */ }
    document.documentElement.setAttribute("data-accent", accent);
    const el = document.querySelector(".chat-redesign") as HTMLElement | null;
    if (el) el.setAttribute("data-accent", accent);
  }, [accent]);

  useEffect(() => {
    try { localStorage.setItem("mng-bg", bg); } catch { /* ignore */ }
    document.documentElement.setAttribute("data-bg", bg);
    const el = document.querySelector(".chat-redesign") as HTMLElement | null;
    if (el) el.setAttribute("data-bg", bg);
  }, [bg]);

  return (
    <div className="profile-card">
      <div className="profile-section">
        <div className="profile-section-label">Цвет акцента</div>
        <div className="mng-rp-colors">
          {(["green", "blue", "purple", "teal", "orange", "pink", "red", "indigo", "graphite"] as const).map(c => (
            <button key={c} type="button"
              className={`mng-rp-color is-${c}${accent === c ? " active" : ""}`}
              onClick={() => setAccent(c)} title={c}/>
          ))}
        </div>
      </div>

      <div className="profile-divider"/>

      <div className="profile-section">
        <div className="profile-section-label">Фон чата</div>
        <div className="mng-rp-bgs">
          {(["green", "blue", "purple", "beige", "gray", "mint", "peach", "lavender", "sky", "white"] as const).map(b => (
            <button key={b} type="button"
              className={`mng-rp-bg is-${b}${bg === b ? " active" : ""}`}
              onClick={() => setBg(b)} title={b}/>
          ))}
        </div>
      </div>
    </div>
  );
}


function SettingsTab() {
  return (
    <div className="profile-card">
      <NotificationsBlock/>
      <div className="profile-divider"/>
      <MfaBlock/>
      <div className="profile-divider"/>
      <TelegramBlock/>
      <div className="profile-divider"/>
      <ResetCacheBlock/>
    </div>
  );
}


type MfaStatus = {
  enabled: boolean;
  setup_pending: boolean;
  recovery_codes_remaining: number;
};

type MfaSetup = {
  manual_key: string;
  qr_data_url: string;
};

function MfaBlock() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ["mfa-status"],
    queryFn: () => api<MfaStatus>("/api/mfa/status"),
    staleTime: 30_000,
  });
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = statusQ.data;

  async function beginSetup() {
    if (!password || busy) return;
    setBusy(true); setError(null); setRecoveryCodes([]);
    try {
      const result = await api<MfaSetup>("/api/mfa/setup", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      setSetup(result);
      setPassword("");
      setCode("");
    } catch (e) {
      setError((e as Error).message || "Не удалось начать настройку");
    } finally { setBusy(false); }
  }

  async function confirmSetup() {
    if (code.trim().length < 6 || busy) return;
    setBusy(true); setError(null);
    try {
      const result = await api<{ recovery_codes: string[] }>("/api/mfa/confirm", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setRecoveryCodes(result.recovery_codes);
      setSetup(null);
      setCode("");
      await qc.invalidateQueries({ queryKey: ["mfa-status"] });
    } catch (e) {
      setError((e as Error).message || "Неверный одноразовый код");
    } finally { setBusy(false); }
  }

  async function regenerateCodes() {
    if (code.trim().length < 6 || busy) return;
    setBusy(true); setError(null);
    try {
      const result = await api<{ recovery_codes: string[] }>("/api/mfa/recovery-codes", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setRecoveryCodes(result.recovery_codes);
      setCode("");
      await qc.invalidateQueries({ queryKey: ["mfa-status"] });
    } catch (e) {
      setError((e as Error).message || "Не удалось обновить резервные коды");
    } finally { setBusy(false); }
  }

  async function disableMfa() {
    if (code.trim().length < 6 || busy) return;
    if (!window.confirm("Отключить двухфакторную защиту?")) return;
    setBusy(true); setError(null);
    try {
      await api("/api/mfa/disable", {
        method: "POST",
        body: JSON.stringify({ code: code.trim() }),
      });
      setCode(""); setSetup(null); setRecoveryCodes([]);
      await qc.invalidateQueries({ queryKey: ["mfa-status"] });
    } catch (e) {
      setError((e as Error).message || "Не удалось отключить защиту");
    } finally { setBusy(false); }
  }

  async function copyRecoveryCodes() {
    try { await navigator.clipboard.writeText(recoveryCodes.join("\n")); } catch { /* ignore */ }
  }

  return (
    <div className="profile-section">
      <div className="profile-section-title">Двухфакторная защита</div>
      <div className="profile-section-sub">
        {status?.enabled
          ? `Включена · резервных кодов осталось: ${status.recovery_codes_remaining}`
          : "Дополнительный код из Google Authenticator, Microsoft Authenticator или 1Password при входе."}
      </div>

      {recoveryCodes.length > 0 && (
        <div style={{ marginTop: 12, padding: 12, border: "1px solid var(--line)", borderRadius: 10 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Сохраните резервные коды сейчас</div>
          <div className="profile-section-sub" style={{ marginBottom: 8 }}>
            Каждый код работает один раз. После закрытия этого блока сервер их больше не покажет.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
            {recoveryCodes.map((item) => <code key={item} style={{ fontSize: 13 }}>{item}</code>)}
          </div>
          <button type="button" className="profile-btn-ghost" style={{ marginTop: 10 }} onClick={copyRecoveryCodes}>
            Скопировать коды
          </button>
        </div>
      )}

      {!status?.enabled && !setup && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <input className="profile-input" type="password" autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            maxLength={128} placeholder="Текущий пароль" style={{ maxWidth: 260 }}/>
          <button type="button" className="profile-btn-primary" onClick={beginSetup}
            disabled={busy || !password}>Включить</button>
        </div>
      )}

      {setup && (
        <div style={{ marginTop: 12 }}>
          <div className="profile-section-sub">Отсканируйте QR-код приложением-аутентификатором:</div>
          <img src={setup.qr_data_url} alt="QR-код настройки двухфакторной защиты"
            style={{ width: 200, height: 200, background: "#fff", padding: 8, borderRadius: 8, marginTop: 8 }}/>
          <div className="profile-section-sub" style={{ marginTop: 6 }}>
            Ключ для ручного ввода: <code style={{ userSelect: "all" }}>{setup.manual_key}</code>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <input className="profile-input" type="text" inputMode="numeric" autoComplete="one-time-code"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
              maxLength={6} placeholder="123456" style={{ width: 140, letterSpacing: 2, textAlign: "center" }}/>
            <button type="button" className="profile-btn-primary" onClick={confirmSetup}
              disabled={busy || code.trim().length !== 6}>Подтвердить</button>
          </div>
        </div>
      )}

      {status?.enabled && (
        <div style={{ marginTop: 10 }}>
          <input className="profile-input" type="text" autoComplete="one-time-code"
            value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={32} placeholder="Одноразовый или резервный код" style={{ maxWidth: 280 }}/>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button type="button" className="profile-btn-ghost" onClick={regenerateCodes}
              disabled={busy || code.trim().length < 6}>Новые резервные коды</button>
            <button type="button" className="profile-btn-ghost" onClick={disableMfa}
              disabled={busy || code.trim().length < 6}>Отключить</button>
          </div>
        </div>
      )}

      {error && <div className="profile-error" style={{ marginTop: 8 }}>{error}</div>}
    </div>
  );
}


function NotificationsBlock() {
  const qc = useQueryClient();
  const push = usePushNotifications();
  const pushStatusQ = useQuery({
    queryKey: ["push-status"],
    queryFn: fetchPushStatus,
    staleTime: 30_000,
  });
  const notifEnabled = pushStatusQ.data ? !pushStatusQ.data.muted : true;
  const isPushSubscribed = push.state.status === "subscribed";
  const isOn = isPushSubscribed && notifEnabled;

  const muteMut = useMutation({
    mutationFn: muteNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["push-status"] }),
  });
  const unmuteMut = useMutation({
    mutationFn: unmuteNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["push-status"] }),
  });

  async function toggle() {
    if (isOn) {
      try { muteMut.mutate(); } catch { /* ignore */ }
      try { await push.unsubscribe(); } catch (e) { console.warn(e); }
    } else {
      if (!isPushSubscribed) {
        const ok = await push.subscribe();
        if (!ok) return;
      }
      try { unmuteMut.mutate(); } catch { /* ignore */ }
    }
  }

  let statusText: string;
  if (push.state.status === "unsupported") statusText = "Браузер не поддерживает push-уведомления";
  else if (push.state.status === "denied") statusText = "Разрешение на уведомления отклонено в браузере";
  else if (push.state.status === "subscribed") statusText = notifEnabled ? "Уведомления приходят" : "Тихий режим включён";
  else statusText = "Уведомления выключены";

  return (
    <div className="profile-section">
      <div className="profile-section-row">
        <div>
          <div className="profile-section-title">Уведомления</div>
          <div className="profile-section-sub">{statusText}</div>
        </div>
        <button type="button"
          className={`mng-rp-toggle${isOn ? " active" : ""}`}
          onClick={toggle}
          disabled={push.busy || muteMut.isPending || unmuteMut.isPending || push.state.status === "unsupported" || push.state.status === "denied"}
          aria-pressed={isOn}/>
      </div>
    </div>
  );
}


function TelegramBlock() {
  const qc = useQueryClient();
  const tgQ = useQuery({
    queryKey: ["tg-status"],
    queryFn: fetchTelegramStatus,
    staleTime: 30_000,
  });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const bindMut = useMutation({
    mutationFn: (c: string) => bindTelegram(c),
    onSuccess: () => {
      setCode(""); setError(null);
      qc.invalidateQueries({ queryKey: ["tg-status"] });
    },
    onError: (e: Error) => setError(e.message || "Не удалось привязать"),
  });
  const unbindMut = useMutation({
    mutationFn: () => unbindTelegram(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tg-status"] }),
  });

  const bound = tgQ.data?.bound;

  return (
    <div className="profile-section">
      <div className="profile-section-title">Telegram</div>
      {bound ? (
        <>
          <div className="profile-section-sub">
            Привязан: {tgQ.data?.telegram_username ? `@${tgQ.data.telegram_username}` : `id ${tgQ.data?.telegram_id}`}
          </div>
          <button type="button" className="profile-btn-ghost" style={{ marginTop: 10 }}
            onClick={() => unbindMut.mutate()} disabled={unbindMut.isPending}>
            Отвязать
          </button>
        </>
      ) : (
        <>
          <div className="profile-section-sub">
            Откройте бота <a href="https://t.me/kb_dashboard_bot" target="_blank" rel="noopener noreferrer" style={{ color: "var(--tg-accent, #4fae4e)" }}>@kb_dashboard_bot</a>, отправьте <code>/start</code> и введите полученный 6-значный код:
          </div>
          <form onSubmit={(e) => { e.preventDefault(); const c = code.trim(); if (c) bindMut.mutate(c); }}
                style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input className="profile-input" type="text" value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6} placeholder="123456"
              style={{ width: 140, fontFamily: "monospace", letterSpacing: 2, textAlign: "center" }}/>
            <button type="submit" className="profile-btn-primary"
              disabled={bindMut.isPending || code.trim().length < 4}>
              Привязать
            </button>
          </form>
          {error && <div className="profile-error" style={{ marginTop: 8 }}>{error}</div>}
        </>
      )}
    </div>
  );
}


function ResetCacheBlock() {
  const [busy, setBusy] = useState(false);
  async function reset() {
    if (busy) return;
    setBusy(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) {
          try { await r.unregister(); } catch { /* ignore */ }
        }
      }
      if (window.caches) {
        const ks = await caches.keys();
        await Promise.all(ks.map(k => caches.delete(k)));
      }
      window.location.reload();
    } catch (e) {
      console.warn("reset cache failed", e);
      setBusy(false);
    }
  }
  return (
    <div className="profile-section">
      <div className="profile-section-title">Сбросить кэш PWA</div>
      <div className="profile-section-sub">
        Удаляет Service Worker и кэш браузера, перезагружает приложение со свежим кодом. Подписка на push сохранится после повторного входа.
      </div>
      <button type="button" className="profile-btn-ghost" style={{ marginTop: 10 }}
        onClick={reset} disabled={busy}>
        <Icon name="refresh" size={12}/> {busy ? "…" : "Сбросить кэш"}
      </button>
    </div>
  );
}

export default ProfilePage;
